import { T, formatCents, tt } from '@agos/shared'
import { Plus, X } from 'lucide-react'
import { useState } from 'react'

import { addonGroups, addonsConfirmLabel, addonsTotalCents, type Addon } from './addons'

/**
 * *Chcete si priobjednať niečo navyše?* — asked once, after the product is already in the basket.
 *
 * The order matters and is legacy's: **add first, ask second**. A customer who dismisses this still has
 * what they came for, and one who never sees it is not blocked. It is an offer, not a step.
 *
 * Add-ons are ordinary products — their own id, price and VAT — so choosing one puts it in the basket as
 * itself. There is no add-on line concept anywhere, because the kitchen makes a dressing and the receipt
 * prints a dressing.
 */
export function AddonPrompt({
  productName,
  addons,
  onConfirm,
  onClose,
}: {
  productName: string
  addons: readonly Addon[]
  onConfirm: (chosen: number[]) => void
  onClose: () => void
}) {
  const [chosen, setChosen] = useState<ReadonlySet<number>>(new Set())
  const total = addonsTotalCents(addons, chosen)

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      data-addon-prompt
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={tt('Chcete si priobjednať niečo navyše?')}
        onClick={(event) => event.stopPropagation()}
        className="w-full sm:max-w-sm bg-background rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col"
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4 shrink-0">
          <div className="min-w-0">
            <h2 className="font-bold text-lg leading-tight">
              <T>Chcete si priobjednať niečo navyše?</T>
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5 truncate">{productName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-addon-close
            aria-label={tt('Zavrieť')}
            className="p-2 -mr-2 rounded-full hover:bg-secondary transition-colors shrink-0"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* §9.70 - grouped, because twenty in one column makes a dressing and a drink look like the
          * same kind of decision. Uncategorised sit last under no heading: they are the ones nobody
          * filed, not a category called *(žiadna)*. */}
        <div className="px-6 pb-2 space-y-3 overflow-y-auto flex-1">
          {addonGroups(addons).map((group) => (
            <div key={group.name ?? '(none)'} className="space-y-2" data-addon-group={group.name ?? ''}>
              {group.name !== null && (
                <p className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  {group.name}
                </p>
              )}
              {group.addons.map((addon) => {
            const picked = chosen.has(addon.id)
            return (
              <label
                key={addon.id}
                data-addon={addon.id}
                className={`flex items-center gap-3 p-3 rounded-2xl border cursor-pointer transition-colors ${
                  picked ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'
                }`}
              >
                <input
                  type="checkbox"
                  checked={picked}
                  onChange={(event) => {
                    const next = new Set(chosen)
                    if (event.target.checked) next.add(addon.id)
                    else next.delete(addon.id)
                    setChosen(next)
                  }}
                  className="w-5 h-5 rounded accent-primary shrink-0"
                />
                {addon.imageUrl !== null && (
                  // Half of them have one. A row that reserved space for the other half would be a
                  // column of grey boxes, so the picture appears only where there is a picture.
                  <img
                    src={addon.imageUrl}
                    alt=""
                    loading="lazy"
                    data-addon-image={addon.id}
                    className="w-10 h-10 rounded-lg object-cover shrink-0"
                  />
                )}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-semibold truncate">{addon.name}</span>
                  {addon.description !== null && (
                    <span className="block text-xs text-muted-foreground truncate">{addon.description}</span>
                  )}
                </span>
                <span className="text-sm font-bold shrink-0">{formatCents(addon.priceCents)}</span>
              </label>
            )
              })}
            </div>
          ))}
        </div>

        <div className="px-6 py-4 border-t border-border shrink-0 space-y-2">
          {total > 0 && (
            <p className="text-sm font-bold text-right" data-addon-total>
              +{formatCents(total)}
            </p>
          )}
          <button
            type="button"
            data-addon-confirm
            onClick={() => onConfirm([...chosen])}
            className="w-full min-h-[48px] rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold flex items-center justify-center gap-2 transition-colors"
          >
            {chosen.size > 0 && <Plus className="w-4 h-4 stroke-[3]" />}
            {addonsConfirmLabel(chosen.size)}
          </button>
        </div>
      </div>
    </div>
  )
}
